import React from 'react';
import {Platform, Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';
import {useTranslation} from 'react-i18next';

import {Divider} from '@/shared/ui/card';
import {AppText} from '@/shared/ui/text';
import {toast} from '@/shared/ui/toast';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Read-only detail rows — the "what do we know about this person / device" block that
 * three separate screens were each re-implementing as `{gap: 4}` inside a padded box.
 *
 * The label is above the value, not beside it: values here are long (a list of
 * departments, a device name, a NFC uid) and a two-column layout either truncates them
 * or leaves a ragged gutter. One card, hairline-separated rows, is what a phone settings
 * screen does with exactly this content.
 */
const styles = StyleSheet.create({
  group: {
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  row: {paddingHorizontal: spacing(3), paddingVertical: spacing(2), gap: 2},
  inline: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  grow: {flex: 1},
});

export function FieldGroup({
  children,
  style,
  testID,
}: {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const rows = React.Children.toArray(children).filter(Boolean);
  return (
    <View
      testID={testID}
      style={[styles.group, {backgroundColor: p.card, borderColor: p.border}, style]}>
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <Divider /> : null}
          {row}
        </React.Fragment>
      ))}
    </View>
  );
}

export function Field({
  label,
  value,
  /** Machine data (ids, codes, addresses) — rendered tabular so digits align. */
  mono = false,
  /** A quiet line under the value: where the value came from, what it implies. */
  note,
  /** Anything to the right of the value — a pill, a small action. */
  trailing,
  /**
   * A value people dictate over the phone or paste into a ticket (MAC, machine
   * number). On the web a press copies it; on a phone a long press selects it —
   * the app carries no native clipboard dependency (see InviteSheet).
   */
  copyable = false,
  testID,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  note?: string | null;
  trailing?: React.ReactNode;
  copyable?: boolean;
  testID?: string;
}) {
  const {t} = useTranslation();
  // An em dash, not an empty row: "we asked and there is nothing" and "we did not ask"
  // must not look the same.
  const present = !!value && value.length > 0;
  const shown = present ? value : '—';
  const text = (
    <AppText variant={mono ? 'mono' : 'body'} style={styles.grow} selectable={copyable && present}>
      {shown}
    </AppText>
  );
  const copy = webClipboard();
  return (
    <View style={styles.row} testID={testID}>
      <AppText variant="label">{label}</AppText>
      <View style={styles.inline}>
        {copyable && present && copy ? (
          <Pressable
            style={styles.grow}
            accessibilityRole="button"
            accessibilityHint={t('actions.copy')}
            onPress={() =>
              copy(value!).then(
                () => toast.success(t('actions.copied')),
                () => undefined,
              )
            }>
            {text}
          </Pressable>
        ) : (
          text
        )}
        {trailing}
      </View>
      {note ? <AppText variant="caption">{note}</AppText> : null}
    </View>
  );
}

/** The browser's clipboard, where there is one (web build, secure context). */
function webClipboard(): ((text: string) => Promise<void>) | null {
  if (Platform.OS !== 'web') return null;
  const clip = (globalThis as {navigator?: {clipboard?: {writeText?: (s: string) => Promise<void>}}})
    .navigator?.clipboard;
  return clip?.writeText ? (s) => clip.writeText!(s) : null;
}
